import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Field, PhotoUpload } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Settings() {
  const { settings, setSettings } = useApp();
  const [f, setF] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(settings); }, [settings]);
  if (!f) return <p className="text-stone-500">Cargando…</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...f, tax_rate: Number(f.tax_rate || 0), quote_validity_days: Number(f.quote_validity_days || 0) });
      setSettings(data);
      toast.success("Datos de la empresa guardados");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} data-testid="settings-form">
      <PageHeader title="Configuración de la empresa" subtitle="Estos datos y el logotipo aparecen en tus notas de venta y cotizaciones">
        <Button type="submit" disabled={saving} className="bg-amber-700 hover:bg-amber-800" data-testid="settings-save-button">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Guardar cambios
        </Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border bg-white p-6 space-y-4">
          <h3 className="font-bold">Identidad</h3>
          <Field label="Logotipo"><PhotoUpload value={f.logo_path} onChange={set("logo_path")} testId="logo" label="Subir logotipo" /></Field>
          <Field label="Nombre de la empresa *"><Input required value={f.name} onChange={set("name")} data-testid="settings-name-input" /></Field>
          <Field label="RFC / ID fiscal"><Input value={f.rfc} onChange={set("rfc")} data-testid="settings-rfc-input" /></Field>
          <Field label="Dirección"><Input value={f.address} onChange={set("address")} data-testid="settings-address-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Teléfono"><Input value={f.phone} onChange={set("phone")} data-testid="settings-phone-input" /></Field>
            <Field label="Correo"><Input type="email" value={f.email} onChange={set("email")} data-testid="settings-email-input" /></Field>
          </div>
          <Field label="Sitio web / redes"><Input value={f.website} onChange={set("website")} data-testid="settings-website-input" /></Field>
        </section>
        <section className="rounded-xl border bg-white p-6 space-y-4">
          <h3 className="font-bold">Documentos</h3>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Moneda (símbolo)"><Input value={f.currency_symbol} onChange={set("currency_symbol")} data-testid="settings-currency-input" /></Field>
            <Field label="Impuesto"><Input value={f.tax_name} onChange={set("tax_name")} data-testid="settings-tax-name-input" /></Field>
            <Field label="Tasa %"><Input type="number" step="any" min="0" value={f.tax_rate} onChange={set("tax_rate")} data-testid="settings-tax-rate-input" /></Field>
          </div>
          <Field label="Vigencia de cotizaciones (días)"><Input type="number" min="0" value={f.quote_validity_days} onChange={set("quote_validity_days")} data-testid="settings-validity-input" /></Field>
          <Field label="Datos bancarios / formas de pago"><Textarea rows={3} value={f.bank_info} onChange={set("bank_info")} placeholder="Banco, cuenta, CLABE…" data-testid="settings-bank-input" /></Field>
          <Field label="Nota al pie (garantía, condiciones)"><Textarea rows={3} value={f.footer_note} onChange={set("footer_note")} data-testid="settings-footer-input" /></Field>
        </section>
      </div>
    </form>
  );
}
